import { useState, useEffect } from 'react';
import { apiFetchBusinessData, apiGetWhitelistExperiences, apiGetDeployedServices } from '../helpers/apiHelpers';
import { buildLabelsIndex } from '../helpers/labels';
import { useStableAccessToken } from './useStableAccessToken';

interface Dependency {
  name: string;
  required: boolean;
  endpoints?: { method: string; resource: string }[];
}

interface Labels {
  name: string;
  value: string;
}

interface Release {
  name: string;
  type: string;
  metadata: any;
  dependencies: Dependency[];
  repository: string;
  main_branches: string[];
  part_of_product?: string[];
  labels?: Labels[];
}

interface BusinessData {
  releases: Release[];
}

export function useBusinessData(applicationBranch: string, deploymentBranch: string, skipQuota: boolean, namespace: string) {
  const tokenGetter = useStableAccessToken();
  const [businessData, setBusinessData] = useState<BusinessData | null>(null);
  const [loadingData, setLoadingData] = useState(false);
  const [fetchingMaster, setFetchingMaster] = useState(false);
  const [featureBusinessData, setFeatureBusinessData] = useState<BusinessData | null>(null);
  const [masterBusinessData, setMasterBusinessData] = useState<BusinessData | null>(null);
  const [rawFeatureData, setRawFeatureData] = useState<BusinessData | null>(null);
  const [labelsByApp, setLabelsByApp] = useState<Record<string, { found: { name: string; value: string }[]; domainValue: string | null }>>({});
  const [availableExperiences, setAvailableExperiences] = useState<string[]>([]);
  const [showWhitelistWarning, setShowWhitelistWarning] = useState(false);
  const [deployedServices, setDeployedServices] = useState<string[]>([]);

  // Fetch the dynamic business metadata from the endpoint. On mount, immediately fetch "master" data
  useEffect(() => {
    async function fetchMaster() {
      try {
        setFetchingMaster(true);
        setLoadingData(true);
        const data = await apiFetchBusinessData('master', tokenGetter);
        setBusinessData(data);
        setMasterBusinessData(data);
      } catch (err) {
        console.error('Error fetching master data:', err);
      } finally {
        setFetchingMaster(false);
        setLoadingData(false)
      }
    }
    fetchMaster();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  // Whenever user has typed a non-empty branch (not "master"), fetch data again
  useEffect(() => {
    async function fetchFeature() {
      try {
        setLoadingData(true);

        const data = await apiFetchBusinessData(applicationBranch, tokenGetter, deploymentBranch);

        setBusinessData(data);
        setFeatureBusinessData(data);
        setLabelsByApp(buildLabelsIndex(data));
      } catch (err) {
        console.error(`Error fetching data for ${applicationBranch}:`, err);
      } finally {
        setLoadingData(false);
      }
    }
    if (applicationBranch) {
      fetchFeature();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicationBranch, deploymentBranch]); // tokenGetter is stable, don't include in deps

  // recompute available experiences whenever data changes
  useEffect(() => {
    if (!businessData) return;

    async function computeExperiences() {
      const allExperiences = Array.from(
        new Set(businessData!.releases.flatMap(r => r.part_of_product || []))
      ).filter(exp => exp !== '*');

      const branch = (applicationBranch || 'master').toLowerCase();
      const PROTECTED = new Set(['master', 'main', 'integration']);

      if (PROTECTED.has(branch)) {
        const whitelist = await apiGetWhitelistExperiences(branch, tokenGetter);
        if (Array.isArray(whitelist) && whitelist.length > 0) {
          // Only allow whitelisted experiences that exist in metadata
          const filtered = whitelist.filter(exp => allExperiences.includes(exp));
          setAvailableExperiences(filtered);
          setShowWhitelistWarning(true);
          return;
        }
      }

      // Feature branches (or if whitelist is missing) → allow all
      setAvailableExperiences(allExperiences);
      setShowWhitelistWarning(false);
    }

    computeExperiences();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessData, applicationBranch]); // tokenGetter is stable, don't include in deps

  // fetch deployed services for standalone mode
  useEffect(() => {
    if ((skipQuota) && namespace) {
      apiGetDeployedServices(namespace, tokenGetter)
        .then(setDeployedServices)
        .catch(() => setDeployedServices([]));
    } else {
      setDeployedServices([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [namespace, skipQuota]); // tokenGetter is stable, don't include in deps

  return {
    businessData,
    loadingData,
    fetchingMaster,
    featureBusinessData,
    masterBusinessData,
    rawFeatureData,
    labelsByApp,
    availableExperiences,
    showWhitelistWarning,
    deployedServices,
  };
}
